import React, { useMemo } from 'react';

import { Control, FormState, useController } from 'react-hook-form';

import {
  Box,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import type { Tag } from '~/services/TagsService';

import TagAssignments, { type AssignmentFormData } from './TagAssignments/TagAssignments';
import TagFormHeader from './TagFormHeader';
import TagSchedules from './TagSchedules/TagSchedules';
import { TAG_TYPE_OPTIONS, TagTypeValue, isContentTag } from './constants';
import { SectionContentStyled, SectionHeaderStyled, SectionStyled } from './styles';

export interface TagDraftSchedule {
  localId: string;
  id?: string;
  tagId?: string;
  brands: Array<string>;
  regions: Array<string>;
  activeStartDate: string;
  activeEndDate: string;
  travelStartDate?: string;
  travelEndDate?: string;
}

export interface FormTag {
  name: string;
  tagType: TagTypeValue;
  schedules: Array<TagDraftSchedule>;
}

interface Props {
  mode: 'create' | 'edit';
  tag?: Tag;
  isSaving: boolean;
  control: Control<FormTag>;
  formState?: FormState<FormTag>;
  assignments: Array<AssignmentFormData>;
  onAssignmentsChange: (assignments: Array<AssignmentFormData>) => void;
  initialAssignments?: Array<AssignmentFormData>;
  onBack: () => void;
  onSave: () => void;
  onViewContent?: () => void;
}

export default function TagForm({
  mode,
  tag,
  isSaving,
  control,
  formState,
  assignments,
  onAssignmentsChange,
  initialAssignments = [],
  onSave,
  onViewContent,
  onBack,
}: Props) {
  const {
    field: nameField,
    fieldState: { error: nameError },
  } = useController({
    control,
    name: 'name',
    rules: {
      required: 'Please enter a tag name',
      pattern: {
        value: /^[a-z][a-z0-9]*(_[a-z][a-z0-9]*)*$/,
        message: 'Name must be lowercase words separated by underscores (e.g. disney_world_hotel)',
      },
    },
  });

  const {
    field: tagTypeField,
    fieldState: { error: tagTypeError },
  } = useController({
    control,
    name: 'tagType',
    rules: {
      required: 'Please select a tag type',
    },
  });

  const contentTag = isContentTag(tagTypeField.value);

  const {
    field: schedulesField,
    fieldState: { error: schedulesError },
  } = useController({
    control,
    name: 'schedules',
    rules: mode === 'edit' && !contentTag ? { required: 'Please add at least one schedule' } : undefined,
  });

  const handleTagTypeChange = (tagType: TagTypeValue) => {
    tagTypeField.onChange(tagType);

    if (isContentTag(tagType)) {
      onAssignmentsChange(assignments.filter((assignment) => assignment.entityType !== 'HOTEL'));
    }
  };

  const handleSchedulesChange = (newSchedules: Array<TagDraftSchedule>) => {
    schedulesField.onChange(newSchedules);
  };

  const hasAssignmentChanges = useMemo(() => {
    if (mode !== 'edit' || initialAssignments.length === 0) {
      return false;
    }
    const initialMap = new Map(initialAssignments.map((a) => [`${a.entityType}-${a.entityId}`, a]));
    return assignments.some((a) => !initialMap.has(`${a.entityType}-${a.entityId}`));
  }, [mode, initialAssignments, assignments]);

  const hasChanges =
    mode === 'edit' &&
    (formState?.dirtyFields.name ||
      formState?.dirtyFields.tagType ||
      formState?.dirtyFields.schedules ||
      hasAssignmentChanges);

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <TagFormHeader
        mode={mode}
        isSaving={isSaving}
        isSaveDisabled={mode === 'edit' ? !hasChanges : false}
        nameInput={
          <TextField
            label="Name"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            inputRef={nameField.ref}
            required
            fullWidth
            error={!!nameError}
            disabled={mode === 'edit'}
            helperText={nameError?.message || 'Use lowercase words separated by underscores (e.g. disney_world_hotel)'}
          />
        }
        tagTypeInput={
          <FormControl fullWidth required error={!!tagTypeError}>
            <InputLabel id="tag-type-select-label">Tag type</InputLabel>
            <Select
              labelId="tag-type-select-label"
              label="Tag type"
              value={tagTypeField.value}
              onChange={(event) => handleTagTypeChange(event.target.value as TagTypeValue)}
              onBlur={tagTypeField.onBlur}
              inputRef={tagTypeField.ref}
            >
              {TAG_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {tagTypeError?.message ||
                TAG_TYPE_OPTIONS.find((option) => option.value === tagTypeField.value)?.description}
            </FormHelperText>
          </FormControl>
        }
        onBack={onBack}
        onSave={onSave}
        onViewContent={onViewContent}
      />
      <Box mt={3}>
        <TagAssignments
          tagId={tag?.id}
          assignments={assignments}
          tagType={tagTypeField.value}
          onAssignmentsChange={onAssignmentsChange}
        />
        {!contentTag && (
          <Box mt={4}>
            <SectionStyled>
              <SectionHeaderStyled>
                <Typography component="h2" variant="h4" fontWeight="bold">
                  Schedules during which to run this tag
                </Typography>
                {(schedulesField.value?.length ?? 0) > 0 && (
                  <Typography variant="body2" ml={2} color="text.secondary">
                    ({schedulesField.value?.length} selected)
                  </Typography>
                )}
              </SectionHeaderStyled>
              <SectionContentStyled>
                <TagSchedules
                  tagId={tag?.id}
                  schedules={schedulesField.value || []}
                  persistOnSave
                  onSchedulesChange={handleSchedulesChange}
                />
                {schedulesError && (
                  <Typography variant="body2" color="error" mt={1}>
                    {schedulesError.message}
                  </Typography>
                )}
              </SectionContentStyled>
            </SectionStyled>
          </Box>
        )}
      </Box>
    </Container>
  );
}
